import { A } from "@solidjs/router";
import { createMemo, createSignal, Switch, Match, Show, onMount, Suspense } from "solid-js";
import { ErrorBoundary } from "~/components/error";
import {
  AppUIContextProvider,
  isExtendedPaneVisible,
  NavigationPaneMode,
  useAppUI,
} from "~/components/layouts/minimal/AppUIContext";
import { PromptBarWithStatus, type PromptSubmission } from "~/components/prompt";
import { useLanguageRegion } from "~/hooks/useLanguageRegion";
import { useI18n } from "~/i18n/context";
import { chatPageDictionary, type ChatPageMessages } from "./ChatStreamPage.i18n";
import { useWire } from "~/wire";
import { useSync } from "~/lib/sync";

import images from "./images.json";
import { twMerge } from "tailwind-merge";
import { ChatRenderer } from "~/components/chat/messages/ChatRenderer";
import { ChatHomepage } from "./ChatHomepage";

// Speed for playback, 1.0 is normal speed
const DEFAULT_PLAYBACK_SPEED = 5.0;

const ChatStreamPageContent = () => {
  const { state, actions } = useAppUI();
  const wire = useWire();
  const languageRegion = useLanguageRegion();
  const { currentLanguage, t } = useI18n();

  // Use the sync hook for asset synchronization
  const [syncState, syncActions] = useSync({
    enablePeriodicSync: true,
    syncIntervalMs: 30000, // 30 seconds for testing
    limit: 50,
    autoStartOnAuth: true,
  });

  // Type-safe translation helper for this page
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  const translate = (key: keyof ChatPageMessages, params?: Record<string, any>) => {
    return t(key as string, chatPageDictionary[currentLanguage()] as unknown as Record<string, string>, params);
  };

  const isWaitingForAuth = () => !wire.auth.isReady();

  // Handle stopping streaming
  const handleStopStreaming = () => {
    // TODO: @andi streaming Cancel workflow
    // chat.setStatus(ChatStatus.DONE);
  };

  // Handle prompt submission
  const handlePromptSubmit = (submission: PromptSubmission) => {
    actions.submitPrompt();

    const workspaceId = wire.auth.realm().workspaceId;
    const projectId = wire.auth.realm().projectId;

    // Start the chat stream with the user's prompt, language region, and scope
    const scope = wire.chat.scope.getScopeForPromptSubmit();
    const locale = languageRegion();
    const submissionContent = submission.text;

    wire.chat.startOrContinueChat({
      submission: submissionContent,
      locale,
      projectId,
      workspaceId,
      scope,
    });
  };

  // TODO: @andi streaming Add playback

  const marginLeft = "margin-left";
  const marginRight = "margin-right";
  // Calculate layout margins to prevent overlap with panes
  const layoutStyles = createMemo(() => {
    const navWidth = state.navigationPane.mode === NavigationPaneMode.DOCKED ? state.navigationPane.width : 0;
    const extendedPaneWidth = isExtendedPaneVisible(state.extendedPane) ? state.extendedPane.width : 0;

    return {
      [marginLeft]: `${navWidth}px`,
      [marginRight]: `${extendedPaneWidth}px`,
    };
  });

  const initial = 22; //3;
  const [imageIndex, setImageIndex] = createSignal(initial);
  const nextImage = () => {
    if (imageIndex() < images.length - 1) {
      setImageIndex(imageIndex() + 1);
    } else {
      setImageIndex(0);
    }
  };
  const previousImage = () => {
    if (imageIndex() > 0) {
      setImageIndex(imageIndex() - 1);
    } else {
      setImageIndex(images.length - 1);
    }
  };

  onMount(() => {
    window.addEventListener("keydown", (e) => {
      if (e.key === "ArrowRight" && e.metaKey && e.ctrlKey) {
        nextImage();
      } else if (e.key === "ArrowLeft" && e.metaKey && e.ctrlKey) {
        previousImage();
      }
    });
  });

  const s = layoutStyles();

  return (
    <Suspense fallback={<LoadingPlayback />}>
      <div class="relative h-screen flex flex-col bg-canvas overflow-hidden">
        {/* Background Image - always present but fades based on state */}
        <div
          class={twMerge(
            "fixed z-0 inset-0 bg-cover bg-center bg-no-repeat pointer-events-none transition-opacity duration-1000 opacity-20 dark:opacity-30",
            wire.chat.shouldShowContent() && "opacity-0 dark:opacity-0",
          )}
          style={{
            "background-image": `url(/images/bg/${images[imageIndex()][2]})`,
          }}
        />
        {/* footer - only visible in initial state */}
        <div
          class="fixed z-20 bottom-0 left-0 right-0 text-sm bg-inset text-muted-text px-3 py-2 rounded-lg backdrop-blur-sm transition-opacity duration-1000"
          style={{
            opacity: wire.chat.shouldShowContent() ? "0" : "1",
          }}
        >
          <div class="flex flex-col items-center text-center">
            <p class="mb-0">
              <span class="hidden sm:inline">
                Enjoy this image of{" "}
                <a href={images[imageIndex()][1]} class="underline hover:text-brand" target="_blank" rel="noreferrer">
                  {images[imageIndex()][0]}
                </a>
                <span class="inline-block border border-gray-200 dark:border-gray-700 rounded-md px-1.5 py-0.5 ml-2 text-xs font-mono bg-gray-50 dark:bg-gray-800/50">
                  CMD+CTRL →
                </span>{" "}
                for next image |
              </span>{" "}
              {translate("agreement_prefix")}{" "}
              <A href="https://go.Storytell.ai/TOS" class="underline hover:text-brand">
                {translate("terms")}
              </A>{" "}
              {translate("and")}{" "}
              <A href="https://go.Storytell.ai/privacy" class="underline hover:text-brand">
                {translate("privacy_policy")}
              </A>
              .
            </p>
          </div>
        </div>

        <Switch>
          <Match when={!wire.chat.shouldShowContent()}>
            <ChatHomepage onSubmit={handlePromptSubmit} />
          </Match>
          <Match when={true}>
            <main
              class="flex-1"
              style={{
                height: "0",
                [marginLeft]: s[marginLeft],
                [marginRight]: s[marginRight],
              }}
            >
              <div class="h-full overflow-y-scroll">
                <div class="max-w-4xl mx-auto px-4 pt-18 pb-32">
                  <ErrorBoundary>
                    <Suspense fallback={<LoadingPlayback />}>
                      <Show when={wire.chat.getChatId()} fallback={<LoadingPlayback />}>
                        {(chatId) => <ChatRenderer chatId={chatId()} />}
                      </Show>
                    </Suspense>
                  </ErrorBoundary>
                </div>
              </div>

              {/* Fixed Blur backdrop to hide any messages between the prompt bar and the viewport bottom  */}
              <div
                aria-hidden
                class="absolute bottom-0 left-0 right-0 h-25 pointer-events-none backdrop-blur-xs bg-canvas/20"
              />
              {/* Fixed Prompt Bar - positioned within same container as StreamRenderer */}
              <div class="absolute bottom-6 left-0 right-0 pointer-events-none pl-4 pr-10">
                <div class="w-full mx-auto space-y-4 px-4 sm:px-6 md:px-8 lg:px-0 pointer-events-auto">
                  <div class="max-w-4xl mx-auto">
                    {/* TODO: @andi streaming Add streaming state */}
                    <PromptBarWithStatus onSubmit={handlePromptSubmit} onStopStreaming={handleStopStreaming} />
                  </div>
                </div>
              </div>
            </main>
          </Match>
        </Switch>
      </div>
    </Suspense>
  );
};

export const ChatStreamPage = () => {
  return (
    <AppUIContextProvider>
      <ChatStreamPageContent />
    </AppUIContextProvider>
  );
};

const LoadingPlayback = () => {
  return (
    <div data-testid="loading-playback-state" class="flex-1 flex flex-col items-center justify-center relative z-10">
      <div class="max-w-md mx-auto px-6">
        <div class="rounded-3xl border border-primary-border backdrop-blur-xl bg-tile p-12 text-center">
          <div class="animate-spin rounded-full h-16 w-16 border-b-2 border-brand mx-auto mb-6" />
          <p class="text-xl text-primary-text font-medium mb-2">Loading...</p>
          <p class="text-sm text-secondary-text">Preparing your conversation</p>
        </div>
      </div>
    </div>
  );
};
